import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UproduktiveSager } from '../../models/uproduktiveSager';
import { StatistikTidsforbrugViewModel } from '../../models/statistikTidsforbrugViewModel';
import { ExcelService } from '@store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
    StatistikPlantidModalComponent
} from '../statistik-plantid-modal/statistik-plantid-modal.component';
import {
    StatistikTidsforbrugIkkeKunderelateretTidModalComponent
} from '../statistik-tidsforbrug-ikke-kunderelateret-tid-modal/statistik-tidsforbrug-ikke-kunderelateret-tid-modal.component';
import { TotalIkkeKundeTidPipe } from './total-ikke-kunde-tid.pipe';
import { firstDayThisMonth, lastDayThisMonth } from '../../../shared/month-picker/month-picker.helper';
import { saveAs } from 'file-saver';

@Component({
    selector: 'app-statistik-tidsforbrug-ui',
    templateUrl: './statistik-tidsforbrug.component.html',
    styleUrls: ['./statistik-tidsforbrug.component.scss'],
    providers: [TotalIkkeKundeTidPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StatistikTidsforbrugComponent implements OnInit {
    @Input() uproduktiveSager: UproduktiveSager[];
    @Input() medarbejdere: StatistikTidsforbrugViewModel[];
    @Input() periode: {fra: string, til: string};
    @Output() periodeChange = new EventEmitter<{fra: string, til: string}>();
    selectedGruppeId: number;
    selectedAfdelingId: number;

    constructor(
        private modalService: NgbModal,
        private excel: ExcelService,
        private totalIkkeKundeTidPipe: TotalIkkeKundeTidPipe,
    ) { }

    ngOnInit() {
        const newPeriode = {
            fra: firstDayThisMonth(),
            til: lastDayThisMonth()
        };
        this.onPeriodeChange(newPeriode);
    }

    onPeriodeChange(periode: { fra: string; til: string }) {
        this.medarbejdere = [];
        this.periodeChange.emit(periode);
    }

    onSelectGruppe(id: number) {
        this.selectedGruppeId = id;
    }

    onSelectAfdeling(id: number) {
        this.selectedAfdelingId = id;
     }

    isMedarbejderInSelectedGruppe(medarbejder: StatistikTidsforbrugViewModel): boolean {
        return medarbejder.grupper
            ?.split(',')
            .map(gruppe => +gruppe)
            .includes(this.selectedGruppeId);
    }
    isMedarbejderInSelectedAfdeling(medarbejder: StatistikTidsforbrugViewModel): boolean {
        return medarbejder.afdeling === this.selectedAfdelingId;
    }

    trackMedarbejderInit(_, medarbejder: StatistikTidsforbrugViewModel) {
        return medarbejder.init;
    }

    trackUproduktiveSagPID(_, sag: UproduktiveSager) {
        return sag.pid;
    }

    calcTotals(variable: string): number {
        let total = 0;

        if (this.medarbejdere) {
            switch (variable) {
                case 'plan_tid_kunder': {
                    this.medarbejdere.forEach(medarbejder => {
                        if ((this.isMedarbejderInSelectedGruppe(medarbejder) || !this.selectedGruppeId)
                            && (this.isMedarbejderInSelectedAfdeling(medarbejder) || !this.selectedAfdelingId)) {
                            total += medarbejder.plan_tid_kunder;
                        }
                    });
                    return total;
                }
                case 'kundeRelateret': {
                    this.medarbejdere.forEach(medarbejder => {
                        if ((this.isMedarbejderInSelectedGruppe(medarbejder) || !this.selectedGruppeId)
                            && (this.isMedarbejderInSelectedAfdeling(medarbejder) || !this.selectedAfdelingId)) {
                            total += medarbejder.kundeRelateret;
                        }
                    });
                    return total;
                }
                case 'brugt-tid-ialt': {
                    this.medarbejdere.forEach(medarbejder => {
                        if ((this.isMedarbejderInSelectedGruppe(medarbejder) || !this.selectedGruppeId)
                            && (this.isMedarbejderInSelectedAfdeling(medarbejder) || !this.selectedAfdelingId)) {
                            total += medarbejder.total;
                        }
                    });
                    return total;
                }
                case 'norm-tid': {
                    this.medarbejdere.forEach(medarbejder => {
                        if ((this.isMedarbejderInSelectedGruppe(medarbejder) || !this.selectedGruppeId)
                            && (this.isMedarbejderInSelectedAfdeling(medarbejder) || !this.selectedAfdelingId)) {
                            total += medarbejder.normtid;
                        }
                    });
                    return total;
                }
                case '+-tid': {
                    this.medarbejdere.forEach(medarbejder => {
                        if ((this.isMedarbejderInSelectedGruppe(medarbejder) || !this.selectedGruppeId)
                            && (this.isMedarbejderInSelectedAfdeling(medarbejder) || !this.selectedAfdelingId)) {
                            total += medarbejder.total - medarbejder.normtid;
                        }
                    });
                    return total;
                }
                default: {
                    return total;
                }
            }
        }
        return total;
    }

    calcUproduktiveSagTotals(uproduktiveSag: UproduktiveSager) {
        let total = 0;
        if (this.medarbejdere) {
            this.medarbejdere.forEach(medarbejder => {
                if ((this.isMedarbejderInSelectedGruppe(medarbejder) || !this.selectedGruppeId)
                    && (this.isMedarbejderInSelectedAfdeling(medarbejder) || !this.selectedAfdelingId)) {
                    medarbejder.ikkeKundeTid.forEach(ikt => {
                        if (ikt.pid === uproduktiveSag.pid) {
                            total += ikt.tid;
                        }
                    });
                }
            });
        }
        return total;
    }

    async openPlantidModal(medarbejder: StatistikTidsforbrugViewModel) {
        if (medarbejder.plan_tid_kunder) {
            const plantidModal = this.modalService.open(StatistikPlantidModalComponent, {size: 'lg'});
            plantidModal.componentInstance.medarbejder = medarbejder;
            plantidModal.componentInstance.medid = medarbejder.medID;
            plantidModal.componentInstance.medarbejderName = medarbejder.init;
            plantidModal.componentInstance.selectedPeriod = this.periode;
        }
    }
    async openIKRTModal(medarbejder: StatistikTidsforbrugViewModel, pid: UproduktiveSager) {
        const result = this.totalIkkeKundeTidPipe.transform(medarbejder.ikkeKundeTid, pid.pid);

        if (result !== 0) {
            const iKRTModal = this.modalService.open(StatistikTidsforbrugIkkeKunderelateretTidModalComponent, {size: 'lg'});
            iKRTModal.componentInstance.medarbejder = medarbejder;
            iKRTModal.componentInstance.title = pid.beskrivelse;
            iKRTModal.componentInstance.pid = pid.pid;
            iKRTModal.componentInstance.selectedPeriod = this.periode;
        }
    }

    onExport() {
        const exportArray = [
            ['Init.', 'Plan tid kunder', 'Kunderelateret', ...this.uproduktiveSager.map(ups => ups.beskrivelse), 'Brugt tid ialt', 'Norm tid', '+-tid'],
            ...this.medarbejdere.map(medarbejder => [
                medarbejder.init,
                medarbejder.plan_tid_kunder,
                medarbejder.kundeRelateret,
                ...this.uproduktiveSager.map(ups => this.totalIkkeKundeTidPipe.transform(medarbejder.ikkeKundeTid, ups.pid)),
                medarbejder.total,
                medarbejder.normtid,
                medarbejder.total - medarbejder.normtid,
            ])
        ];
        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, 'statistik-tidsforbrug.xlsx');
        });
    }
}
